import React, { useState, useEffect } from 'react'
import { Search, Eye, Edit, Clock, ChevronLeft, ChevronRight, RefreshCw, Wifi, WifiOff, ArrowUpDown, X } from 'lucide-react'
// Unused: AlertCircle
import { cn, formatDate } from '@/lib/utils'
import { useErrorHandler } from '@/hooks/useErrorHandler'
// Unused: clearAllErrors from useErrorHandler
import { ToastContainer } from './Toast'
import { apiClient } from '@/lib/api'
import { Button } from './ui/button'
import { Badge } from './ui/badge'

interface Client {
  id: string
  name: string
  status: 'active' | 'inactive'
  context: string
  system_prompt: string
  last_updated: string
  last_synced: string
  mssql_client_id?: string
}

interface ClientManagerProps {
  onClientSelect?: (clientId: string) => void
  className?: string
}

export function ClientManager({
  onClientSelect,
  className
}: ClientManagerProps) {
  const [clients, setClients] = useState<Client[]>([])
  const [loading, setLoading] = useState(true)
  const [updatingClients, setUpdatingClients] = useState<Set<string>>(new Set())
  const [retryCount, setRetryCount] = useState(0)
  const { errors, addError, addWarning, addInfo, clearError } = useErrorHandler()
  // Unused: clearAllErrors
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [sortBy, setSortBy] = useState<'name' | 'status' | 'last_updated'>('name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedClient, setSelectedClient] = useState<Client | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState<'view' | 'edit'>('view')

  const CLIENTS_PER_PAGE = 50

  useEffect(() => {
    loadClients()
  }, [])

  const loadClients = async (isRetry = false) => {
    let timeoutId: number | null = null
    try {
      if (!isRetry) setLoading(true)

      const token = localStorage.getItem('access_token')
      if (!token) {
        addError('Authentication required. Please log in again.')
        setLoading(false)
        return
      }

      const controller = new AbortController()
      timeoutId = setTimeout(() => controller.abort(), 10000) // 10 second timeout

      const data = await apiClient.get('/clients/?limit=1000', {
        signal: controller.signal
      }) as { clients?: any[] }
      if (timeoutId) clearTimeout(timeoutId)

      setClients(data.clients || [])
      setRetryCount(0)
      addInfo('Clients loaded successfully')
    } catch (error: any) {
      if (timeoutId) clearTimeout(timeoutId)

      if (error.message?.includes('401') || error.message?.includes('403')) {
        addError('Authentication failed. Please log in again.')
        localStorage.removeItem('access_token')
        window.location.href = '/login'
        return
      } else if (error.message?.includes('500') || error.message?.includes('Server error')) {
        const errorMessage = `Server error: ${error.message}`
        addError(errorMessage, 'error', () => loadClients(true))
      } else {
        const errorMessage = error.message || 'Failed to load clients'
        addError(errorMessage, 'error', () => loadClients(true))
      }

      if (!isRetry && retryCount < 2) {
        addWarning(`Retrying in ${2 * (retryCount + 1)} seconds...`, () => loadClients(true))
        setTimeout(() => {
          setRetryCount(prev => prev + 1)
          loadClients(true)
        }, 2000 * (retryCount + 1)) // Exponential backoff
      }
    } finally {
      setLoading(false)
    }
  }

  const updateClient = async (clientId: string, updates: Partial<Client>) => {
    setUpdatingClients(prev => new Set(prev).add(clientId))

    let timeoutId: number | null = null
    try {
      const token = localStorage.getItem('access_token')
      if (!token) {
        addError('Authentication required. Please log in again.')
        return false
      }

      const controller = new AbortController()
      timeoutId = setTimeout(() => controller.abort(), 8000) // 8 second timeout for updates

      const data = await apiClient.patch(`/clients/${clientId}`, updates, {
        signal: controller.signal
      }) as any

      if (timeoutId) clearTimeout(timeoutId)

      const updated = (data as any).client || data
      setClients(prev => prev.map(client =>
        client.id === clientId ? { ...client, ...(updated as object) } : client
      ))
      addInfo('Client updated successfully')
      return true
    } catch (error: any) {
      console.error('Error updating client:', error)

      if (timeoutId) clearTimeout(timeoutId)

      if (error.message?.includes('401') || error.message?.includes('403')) {
        addError('Authentication failed. Please log in again.')
        localStorage.removeItem('access_token')
        window.location.href = '/login'
        return false
      } else if (error.message?.includes('404')) {
        addError('Client not found. It may have been deleted.', 'warning')
        return false
      } else if (error.message?.includes('422')) {
        addError(`Validation failed: ${error.message}`)
        return false
      } else if (error.message?.includes('500') || error.message?.includes('Server error')) {
        const errorMessage = `Server error: ${error.message}`
        addError(errorMessage, 'error', () => updateClient(clientId, updates))
      } else {
        const errorMessage = error instanceof Error
          ? (error.name === 'AbortError'
            ? 'Update timed out. Please check your connection.'
            : `Failed to update client: ${error.message}`)
          : 'Failed to update client. Please try again.'
        addError(errorMessage, 'error', () => updateClient(clientId, updates))
      }

      return false
    } finally {
      setUpdatingClients(prev => {
        const newSet = new Set(prev)
        newSet.delete(clientId)
        return newSet
      })
    }
  }

  const openClientModal = (client: Client, mode: 'view' | 'edit' = 'view') => {
    setSelectedClient(client)
    setModalMode(mode)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setSelectedClient(null)
    setModalMode('view')
  }

  const handleSaveClient = async (updates: Partial<Client>) => {
    if (!selectedClient) return false
    
    const success = await updateClient(selectedClient.id, updates)
    if (success) {
      closeModal()
    }
    return success
  }

  // Filter and sort clients
  const filteredClients = React.useMemo(() => {
    let filtered = clients

    // Apply search filter (name only)
    if (searchTerm) {
      filtered = filtered.filter(client =>
        client.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(client => client.status === statusFilter)
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0

      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name)
          break
        case 'status':
          comparison = a.status.localeCompare(b.status)
          break
        case 'last_updated':
          comparison = new Date(a.last_updated).getTime() - new Date(b.last_updated).getTime()
          break
      }

      return sortOrder === 'desc' ? -comparison : comparison
    })

    return filtered
  }, [clients, searchTerm, statusFilter, sortBy, sortOrder])

  useEffect(() => {
    const newTotalPages = Math.max(1, Math.ceil(filteredClients.length / CLIENTS_PER_PAGE))
    if (currentPage < 1) {
      setCurrentPage(1)
      return
    }
    if (currentPage > newTotalPages) {
      setCurrentPage(newTotalPages)
    }
  }, [filteredClients.length, currentPage, CLIENTS_PER_PAGE])

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredClients.length / CLIENTS_PER_PAGE))
  const displayCurrentPage = Math.max(1, Math.min(currentPage, totalPages))
  const startIndex = (displayCurrentPage - 1) * CLIENTS_PER_PAGE
  const paginatedClients = filteredClients.slice(startIndex, startIndex + CLIENTS_PER_PAGE)
  const isPaginationActive = filteredClients.length > CLIENTS_PER_PAGE
  const disablePrev = !isPaginationActive || displayCurrentPage === 1
  const disableNext = !isPaginationActive || displayCurrentPage === totalPages || filteredClients.length === 0
  const showingStart = filteredClients.length === 0 ? 0 : startIndex + 1
  const showingEnd = startIndex + paginatedClients.length

  const statusStyles: Record<Client['status'], { label: string; badgeClass: string; dotClass: string }> = {
    active: {
      label: 'Active',
      badgeClass: 'bg-[#c4e8d1] text-green-600 border-green-600',
      dotClass: 'bg-green-500'
    },
    inactive: {
      label: 'Inactive',
      badgeClass: 'bg-[#ffd1d1] text-red-600 border-red-500',
      dotClass: 'bg-red-500'
    }
  }

  const getStatusStyles = (status: Client['status']) => statusStyles[status] || statusStyles.inactive

  const truncateText = (text: string | null | undefined, maxLength: number = 100) => {
    if (!text) return ''
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Clock className="h-8 w-8 text-muted-foreground mx-auto mb-2 animate-spin" />
          <p className="text-muted-foreground">Loading clients...</p>
          {retryCount > 0 && (
            <p className="text-sm text-muted-foreground mt-2">
              Retrying... (attempt {retryCount + 1}/3)
            </p>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={cn(className)}>
      {/* Header */}
      <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-foreground page-main-heading sm:mb-0">Client Management</h1>

        {clients.length > 0 && (
          <div className="flex items-center text-sm text-gray-600">
            {errors.some(e => e.type === 'error') ? (
              <>
                <WifiOff className="h-4 w-4 text-red-500 mr-1" />
                <span className="text-red-600">Connection issues</span>
              </>
            ) : (
              <>
                <Wifi className="h-4 w-4 text-green-500 mr-1" />
                <span className="text-green-600">Connected</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        {/* Search */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search clients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Status Filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>

        {/* Sort Options */}
        <select
          value={`${sortBy}-${sortOrder}`}
          onChange={(e) => {
            const [sort, order] = e.target.value.split('-')
            setSortBy(sort as any)
            setSortOrder(order as any)
          }}
          className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="name-asc">Name A-Z</option>
          <option value="name-desc">Name Z-A</option>
          <option value="status-asc">Status Active First</option>
          <option value="status-desc">Status Inactive First</option>
          <option value="last_updated-desc">Recently Updated</option>
          <option value="last_updated-asc">Oldest Updated</option>
        </select>

        <Button
          variant="blue"
          onClick={() => loadClients()}
        >
          Refresh
        </Button>
      </div>

      {/* Results Summary */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          {filteredClients.length === 0
            ? `No clients to display${clients.length > 0 ? ` (filtered from ${clients.length} total)` : ''}`
            : `Showing ${showingStart}-${showingEnd} of ${filteredClients.length} clients${filteredClients.length !== clients.length ? ` (filtered from ${clients.length} total)` : ''}`}
        </p>
      </div>

      {/* Client List */}
      {paginatedClients.length > 0 ? (
        <div className="flex flex-col items-start relative w-full">
          <table className="w-full border border-neutral-200 rounded-md">
            <thead>
              <tr className="border-b border-neutral-200 bg-neutral-50">
                <th className="h-10 px-4 py-0 text-left">
                  <button
                    type="button"
                    className="flex items-center gap-2 text-sm font-semibold text-neutral-950 hover:text-blue-600"
                    onClick={() => {
                      if (sortBy === 'name') {
                        setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')
                      } else {
                        setSortBy('name')
                        setSortOrder('asc')
                      }
                    }}
                  >
                    Client
                    <ArrowUpDown className="h-4 w-4" />
                  </button>
                </th>
                <th className="w-[320px] h-10 px-4 py-0 text-left">
                  <span className="text-sm font-semibold text-neutral-950">Бизнес контекст</span>
                </th>
                <th className="w-[160px] h-10 px-4 py-0 text-left">
                  <button
                    type="button"
                    className="flex items-center gap-2 text-sm font-semibold text-neutral-950 hover:text-blue-600"
                    onClick={() => {
                      if (sortBy === 'last_updated') {
                        setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')
                      } else {
                        setSortBy('last_updated')
                        setSortOrder('desc')
                      }
                    }}
                  >
                    Last Updated
                    <ArrowUpDown className="h-4 w-4" />
                  </button>
                </th>
                <th className="w-[160px] h-10 px-4 py-0 text-left">
                  <span className="text-sm font-semibold text-neutral-950">Last Synced</span>
                </th>
                <th className="w-[140px] h-10 px-4 py-0 text-left">
                  <span className="text-sm font-semibold text-neutral-950">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedClients.map((client) => {
                const isUpdating = updatingClients.has(client.id)
                const { label, badgeClass } = getStatusStyles(client.status)

                return (
                  <tr
                    key={client.id}
                    className={cn(
                      "group border-b border-neutral-200 hover:bg-neutral-50 transition-colors cursor-pointer",
                      isUpdating && "opacity-60 pointer-events-none"
                    )}
                    onClick={() => !isUpdating && onClientSelect && onClientSelect(client.id)}
                  >
                    <td className="px-4 py-3 align-top">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground truncate">
                            {client.name}
                          </span>
                          {client.mssql_client_id && (
                            <span className="text-xs text-neutral-400">#{client.mssql_client_id}</span>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          ID: {client.id}
                        </span>
                      </div>
                    </td>
                    <td className="w-[320px] px-4 py-3 align-top">
                      <span className="block text-xs text-muted-foreground">
                        {truncateText(client.context, 140) || '—'}
                      </span>
                    </td>
                    <td className="w-[160px] px-4 py-3 align-top text-sm text-muted-foreground">
                      {formatDate(client.last_updated)}
                    </td>
                    <td className="w-[160px] px-4 py-3 align-top text-sm text-muted-foreground">
                      {formatDate(client.last_synced)}
                    </td>
                    <td className="w-[140px] px-4 py-3 align-top">
                      <div className="flex items-center gap-2">
                        <Badge className={cn("border text-[11px] font-semibold uppercase tracking-wide", badgeClass)}>
                          {label}
                        </Badge>
                        {isUpdating && (
                          <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            openClientModal(client, 'view')
                          }}
                          className="px-2 py-1 text-sm text-muted-foreground hover:text-blue-600"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            openClientModal(client, 'edit')
                          }}
                          className="px-2 py-1 text-sm text-muted-foreground hover:text-green-600"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <div
            className={cn(
              "flex w-full items-center justify-between px-4 py-4",
              !isPaginationActive && "opacity-60"
            )}
          >
            <div className="text-sm text-muted-foreground">
              {filteredClients.length === 0
                ? `No clients to display${clients.length > 0 ? ` (filtered from ${clients.length} total)` : ''}`
                : `Showing ${showingStart}-${showingEnd} of ${filteredClients.length} clients${filteredClients.length !== clients.length ? ` (filtered from ${clients.length} total)` : ''}`}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={disablePrev}
                className="p-2 text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-sm text-gray-600">
                Page {displayCurrentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={disableNext}
                className="p-2 text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="mx-auto w-24 h-24 bg-muted rounded-full flex items-center justify-center mb-4">
            <Search className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">
            {searchTerm || statusFilter !== 'all' ? 'No matching clients' : 'No clients yet'}
          </h3>
          <p className="text-muted-foreground">
            {searchTerm || statusFilter !== 'all' 
              ? 'Try adjusting your search or filter criteria' 
              : 'Clients will appear here when synced from the database'
            }
          </p>
        </div>
      )}
      {/* Client Modal */}
      {selectedClient && (
        <ClientModal
          client={selectedClient}
          isOpen={isModalOpen}
          mode={modalMode}
          onClose={closeModal}
          onSave={handleSaveClient}
        />
      )}

      {/* Toast Notifications */}
      <ToastContainer
        errors={errors}
        onClose={clearError}
      />
    </div>
  )
}

// Client Modal Component
interface ClientModalProps {
  client: Client
  isOpen: boolean
  mode: 'view' | 'edit'
  onClose: () => void
  onSave: (updates: Partial<Client>) => Promise<boolean>
}

function ClientModal({ client, isOpen, mode, onClose, onSave }: ClientModalProps) {
  const [currentMode, setCurrentMode] = useState<'view' | 'edit'>(mode)
  const [formData, setFormData] = useState({
    context: client.context || '',
    system_prompt: client.system_prompt || '',
    status: client.status
  })
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    setCurrentMode(mode)
    setFormData({
      context: client.context || '',
      system_prompt: client.system_prompt || '',
      status: client.status
    })
  }, [client, mode])

  const handleSave = async () => {
    setIsSaving(true)
    const success = await onSave(formData)
    setIsSaving(false)
    
    if (success) {
      setCurrentMode('view')
    }
  }

  const handleCancel = () => {
    if (currentMode === 'edit') {
      setFormData({
        context: client.context || '',
        system_prompt: client.system_prompt || '',
        status: client.status
      })
      setCurrentMode('view')
    } else {
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-background border border-border rounded-lg w-[80vw] h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold text-foreground truncate">
              {currentMode === 'edit' ? 'Edit Client' : 'Client Details'}
            </h2>
            <p className="text-sm text-muted-foreground mt-1 truncate">
              {client.name} • Last synced: {formatDate(client.last_synced)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground flex-shrink-0"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden p-6 flex flex-col gap-4">
          {/* Status Toggle (Edit mode only) */}
          {currentMode === 'edit' && (
            <div className="flex-shrink-0">
              <label className="block text-sm font-medium text-foreground mb-2">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as 'active' | 'inactive' }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          )}

          {/* Side-by-side text areas */}
          <div className="flex-1 grid grid-cols-2 gap-4 min-h-0">
            {/* Business Context */}
            <div className="flex flex-col min-h-0">
              <label className="block text-sm font-medium text-gray-700 mb-1 flex-shrink-0">
                Бизнес контекст
              </label>
              <p className="text-xs text-gray-500 mb-2 flex-shrink-0">
                Опишете дейността на клиента, основни доставчици, валути, ДДС режим, праг за ДА.
              </p>
              <textarea
                value={formData.context}
                onChange={(e) => setFormData(prev => ({ ...prev, context: e.target.value }))}
                readOnly={currentMode === 'view'}
                className={cn(
                  "w-full p-3 border border-gray-300 rounded-md resize-none flex-1",
                  currentMode === 'view' ? 'bg-gray-50' : 'focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                )}
                placeholder={"ИТ компания, специализирана в разработка на софтуер.\n\nОсновни доставчици:\n- Google Ireland Ltd — хостинг и cloud услуги\n- \"Офис Център\" ООД — канцеларски материали\n\nВалути: Основно EUR, фактури от САЩ в USD.\nДДС режим: Стандартен, 20%."}
              />
            </div>

            {/* AI Instructions */}
            <div className="flex flex-col min-h-0">
              <label className="block text-sm font-medium text-gray-700 mb-1 flex-shrink-0">
                AI Инструкции
              </label>
              <p className="text-xs text-gray-500 mb-2 flex-shrink-0">
                Правила за AI при обработка на фактури, класификация на разходи, осчетоводяване.
              </p>
              <textarea
                value={formData.system_prompt}
                onChange={(e) => setFormData(prev => ({ ...prev, system_prompt: e.target.value }))}
                readOnly={currentMode === 'view'}
                className={cn(
                  "w-full p-3 border border-gray-300 rounded-md resize-none flex-1",
                  currentMode === 'view' ? 'bg-gray-50' : 'focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                )}
                placeholder={"- Фактури от Google, AWS, Azure — ВИНАГИ \"Разходи за външни услуги\".\n- Хардуер над 1200 EUR — \"Дълготрайни активи\", под 1200 EUR — \"Материали\".\n- При фактури от ЕС без ДДС → \"Invoice (доставки от ЕС)\"."}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 flex-shrink-0">
          {currentMode === 'view' ? (
            <>
              <Button
                variant="blue"
                onClick={() => setCurrentMode('edit')}
              >
                Edit
              </Button>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-300"
              >
                Close
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-300"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50"
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
